import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  Crown, ShieldCheck, BadgeCheck, Star, TrendingUp, Heart, MapPin, Calendar,
  Car, MessageSquare, Loader2, User, Share2, Copy, ExternalLink
} from 'lucide-react';

// Badge configurations with descriptions
const BADGE_CONFIG = {
  'Verified Dealer': {
    icon: Crown,
    color: 'gold',
    description: 'This seller has an active dealer subscription and has been verified by AutoTrust.'
  },
  'ID Verified': {
    icon: ShieldCheck,
    color: 'green',
    description: 'This seller has completed identity verification (KYC) with a valid Ghana ID.'
  },
  'Trusted Seller': {
    icon: BadgeCheck,
    color: 'blue',
    description: 'This seller has successfully sold 10+ vehicles on AutoTrust with positive feedback.'
  },
  'Active Seller': {
    icon: TrendingUp,
    color: 'purple',
    description: 'This seller has sold 5+ vehicles and is actively listing on the platform.'
  },
  'Top Rated': {
    icon: Star,
    color: 'yellow',
    description: 'This seller maintains a 4.5+ star rating with at least 5 reviews.'
  },
  'Community Favorite': {
    icon: Heart,
    color: 'red',
    description: 'This seller has received 20+ positive reviews from the AutoTrust community.'
  }
};

const BADGE_ICONS = {
  crown: Crown,
  'shield-check': ShieldCheck,
  'badge-check': BadgeCheck,
  star: Star,
  'trending-up': TrendingUp,
  heart: Heart
};

const BADGE_COLORS = {
  gold: 'bg-gold/20 text-gold border-gold/30',
  green: 'bg-trust-green/20 text-trust-green border-trust-green/30',
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export default function SellerProfile() {
  const { userId } = useParams();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');

  useEffect(() => {
    const fetchSeller = async () => {
      try {
        const res = await API.get(`/sellers/${userId}`);
        setSeller(res.data);
      } catch (error) {
        console.error('Error fetching seller:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSeller();
  }, [userId]);

  if (loading) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </main>
    );
  }

  if (!seller) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Seller Not Found</h1>
          <p className="text-muted-foreground mb-6">This seller profile doesn't exist or has been removed.</p>
          <Link to="/browse">
            <Button className="bg-gold text-black">Browse Cars</Button>
          </Link>
        </div>
      </main>
    );
  }

  const memberSinceDate = seller.member_since ? new Date(seller.member_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A';

  return (
    <main className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-2xl p-6 md:p-8 mb-6"
          data-testid="seller-profile-header"
        >
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Avatar & Name */}
            <div className="flex items-center md:items-start gap-4 flex-1">
              <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                {seller.profile_photo ? (
                  <img src={seller.profile_photo} alt={seller.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-gold">{seller.name?.charAt(0) || 'S'}</span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold" data-testid="seller-name">{seller.name}</h1>
                  {seller.verification?.is_dealer && (
                    <Badge className="bg-gold/20 text-gold border-gold/30">
                      <Crown className="w-3 h-3 mr-1" /> Dealer
                    </Badge>
                  )}
                  {seller.verification?.kyc_verified && (
                    <Badge className="bg-trust-green/20 text-trust-green border-trust-green/30">
                      <ShieldCheck className="w-3 h-3 mr-1" /> Verified
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {seller.location || 'Ghana'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Member since {memberSinceDate}
                  </span>
                </div>
                {seller.bio && (
                  <p className="mt-3 text-sm text-muted-foreground max-w-lg">{seller.bio}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex-shrink-0 flex items-center gap-2">
              {/* Share Button - Uses native share API on mobile */}
              {navigator.share ? (
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full" 
                  data-testid="share-profile-btn"
                  onClick={async () => {
                    try {
                      await navigator.share({
                        title: `${seller.name} on AutoTrust Ghana`,
                        text: `Check out ${seller.name}'s cars on AutoTrust Ghana!`,
                        url: window.location.href
                      });
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success('Profile link copied!');
                      }
                    }
                  }}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full" data-testid="share-profile-btn">
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => {
                      const url = window.location.href;
                      const text = `Check out ${seller.name}'s profile on AutoTrust Ghana!`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                    }} className="gap-2 cursor-pointer">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      const url = window.location.href;
                      const text = `Check out ${seller.name}'s cars on AutoTrust Ghana!`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                    }} className="gap-2 cursor-pointer">
                      <ExternalLink className="w-4 h-4" />
                      Twitter / X
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Profile link copied!');
                    }} className="gap-2 cursor-pointer">
                      <Copy className="w-4 h-4" />
                      Copy Link
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {/* Contact Button */}
              <Link to={`/messages?seller=${userId}`}>
                <Button className="bg-gold text-black hover:brightness-110 gap-2" data-testid="contact-seller-btn">
                  <MessageSquare className="w-4 h-4" />
                  Contact Seller
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border/50">
            <div className="text-center">
              <p className="text-2xl font-bold text-gold">{seller.stats?.active_listings || 0}</p>
              <p className="text-xs text-muted-foreground">Active Listings</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-trust-green">{seller.stats?.sold_count || 0}</p>
              <p className="text-xs text-muted-foreground">Items Sold</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-2xl font-bold">{seller.stats?.average_rating || 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">{seller.stats?.review_count || 0} Reviews</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{seller.stats?.total_listings || 0}</p>
              <p className="text-xs text-muted-foreground">Total Listings</p>
            </div>
          </div>

          {/* Badges */}
          {seller.badges && seller.badges.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-border/50">
              <TooltipProvider>
                {seller.badges.map((badge, i) => {
                  const Icon = BADGE_ICONS[badge.icon] || BadgeCheck;
                  const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.blue;
                  const config = BADGE_CONFIG[badge.name];
                  
                  return (
                    <Tooltip key={i}>
                      <TooltipTrigger asChild>
                        <Badge className={`${colorClass} gap-1 cursor-help`} data-testid={`badge-${badge.name.toLowerCase().replace(/\s+/g, '-')}`}>
                          <Icon className="w-3 h-3" />
                          {badge.name}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[250px] p-3">
                        <p className="text-sm">{config?.description || `${badge.name} badge earned by this seller.`}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </TooltipProvider>
            </div>
          )}
        </motion.div>

        {/* Tabs: Listings & Reviews */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="listings" className="gap-2">
              <Car className="w-4 h-4" /> Listings ({seller.listings?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="gap-2">
              <Star className="w-4 h-4" /> Reviews ({seller.stats?.review_count || 0})
            </TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {seller.listings?.length === 0 ? (
              <div className="bg-card border border-border/50 rounded-xl p-8 text-center">
                <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-1">No Active Listings</h3>
                <p className="text-sm text-muted-foreground">This seller doesn't have any active listings at the moment.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="seller-listings">
                {seller.listings?.map((listing) => (
                  <Link key={listing.listing_id} to={`/listing/${listing.listing_id}`}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      className="bg-card border border-border/50 rounded-xl overflow-hidden hover:border-gold/30 transition-colors"
                    >
                      <div className="aspect-[4/3] relative">
                        <img
                          src={listing.photos?.[0] || '/placeholder-car.jpg'}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm rounded-full px-2 py-1 text-xs font-medium flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${listing.trust_score >= 80 ? 'bg-trust-green' : listing.trust_score >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`} />
                          {listing.trust_score}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium truncate">{listing.title}</h3>
                        <p className="text-gold font-mono-display font-semibold mt-1">
                          GHS {listing.price?.toLocaleString()}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>{listing.year}</span>
                          <span>{listing.fuel_type}</span>
                          <span>{listing.mileage?.toLocaleString()} km</span>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            {!seller.reviews || seller.reviews.length === 0 ? (
              <div className="bg-card border border-border/50 rounded-xl p-8 text-center">
                <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-1">No Reviews Yet</h3>
                <p className="text-sm text-muted-foreground">This seller hasn't received any reviews yet.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="seller-reviews">
                {seller.reviews.map((review, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-card border border-border/50 rounded-xl p-5"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                          <span className="font-semibold text-gold">{review.reviewer_name?.charAt(0) || 'U'}</span>
                        </div>
                        <div>
                          <p className="font-medium">{review.reviewer_name || 'Anonymous'}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, j) => (
                              <Star
                                key={j}
                                className={`w-4 h-4 ${j < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="mt-3 text-sm text-muted-foreground">{review.comment}</p>
                    )}
                    {review.listing_title && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        For: <span className="text-foreground">{review.listing_title}</span>
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
